import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { AuthState, AuthUser } from '../types/Auth';

const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID || '';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';

const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: CLIENT_ID,
});

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ needsVerification: boolean }>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signOut: () => void;
  refreshQuota: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    quota: null,
    isLoading: true,
    isAuthenticated: false,
    isAdmin: false,
  });

  const buildUser = (session: any): AuthUser => {
    const idToken = session.getIdToken();
    const groups: string[] = idToken.payload['cognito:groups'] || [];
    return {
      sub: idToken.payload.sub,
      email: idToken.payload.email,
      idToken: idToken.getJwtToken(),
      accessToken: session.getAccessToken().getJwtToken(),
      isAdmin: groups.includes('admin'),
    };
  };

  const loadSession = useCallback(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setState(s => ({ ...s, isLoading: false }));
      return;
    }

    cognitoUser.getSession((err: Error | null, session: any) => {
      if (err || !session?.isValid()) {
        setState(s => ({ ...s, isLoading: false, isAuthenticated: false, user: null, isAdmin: false }));
        return;
      }

      const user = buildUser(session);
      setState(s => ({ ...s, user, isAuthenticated: true, isAdmin: user.isAdmin, isLoading: false }));
    });
  }, []);

  useEffect(() => { loadSession(); }, [loadSession]);

  // Refresh tokens every 45 minutes (tokens expire at 60 min)
  useEffect(() => {
    if (!state.isAuthenticated) return;
    const interval = setInterval(loadSession, 45 * 60 * 1000);
    return () => clearInterval(interval);
  }, [state.isAuthenticated, loadSession]);

  const signIn = (email: string, password: string) =>
    new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      const authDetails = new AuthenticationDetails({ Username: email, Password: password });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess(session) {
          const user = buildUser(session);
          setState(s => ({ ...s, user, isAuthenticated: true, isAdmin: user.isAdmin }));
          resolve();
        },
        onFailure(err) { reject(err); },
        newPasswordRequired() { reject(new Error('Password change required — contact support.')); },
      });
    });

  const signUp = (email: string, password: string) =>
    new Promise<{ needsVerification: boolean }>((resolve, reject) => {
      const attributes = [new CognitoUserAttribute({ Name: 'email', Value: email })];

      userPool.signUp(email, password, attributes, [], (err, result) => {
        if (err) { reject(err); return; }
        resolve({ needsVerification: !result?.userConfirmed });
      });
    });

  const confirmSignUp = (email: string, code: string) =>
    new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) { reject(err); } else { resolve(); }
      });
    });

  const signOut = () => {
    userPool.getCurrentUser()?.signOut();
    setState({ user: null, quota: null, isLoading: false, isAuthenticated: false, isAdmin: false });
  };

  const refreshQuota = useCallback(async () => {
    if (!state.user) return;
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/user/quota`, {
        headers: { Authorization: `Bearer ${state.user.idToken}` },
      });
      if (res.ok) {
        const quota = await res.json();
        setState(s => ({ ...s, quota }));
      }
    } catch {
      // non-fatal
    }
  }, [state.user]);

  useEffect(() => {
    if (state.isAuthenticated) { refreshQuota(); }
  }, [state.isAuthenticated, refreshQuota]);

  return (
    <AuthContext.Provider value={{ ...state, signIn, signUp, confirmSignUp, signOut, refreshQuota }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
